import { isNullOrUndefined } from '../../../isNullOrUndefined';
import { CareMattersRiderForm } from '../calcCareMattersCoverage';
import { CareEnvironment, CarePhase } from '../../../appModel';
import { DAYS_IN_MONTH } from './sharedConstants';

// Universal policy-config readers. Strategies that need a different
// resolution rule can reimplement locally rather than import.

// Max daily benefit across all four care environments, treating unset fields
// as 0. Used both for pool sizing and as the fallback for sparse policies
// (legacy/imported rows that populate only one of the four daily fields —
// common on CM II uploads where the four are equal). Mirrors the legacy
// max-benefit fallback in calcFundingSourceDerivatives.ts.
export function getMaxDailyBenefitAmount(ltcPolicy: PolicyFundingSource): number {
  const home = ltcPolicy.dailyHomeBenefitAmount ?? 0;
  const al = ltcPolicy.dailyAssistedLivingBenefitAmount ?? 0;
  const nh = ltcPolicy.dailyNursingHomeBenefitAmount ?? 0;
  const il = ltcPolicy.dailyIndependentLivingBenefitAmount ?? 0;
  return Math.max(home, al, nh, il);
}

export function getMaxMonthlyBenefitAmount(ltcPolicy: PolicyFundingSource): number {
  return getMaxDailyBenefitAmount(ltcPolicy) * DAYS_IN_MONTH;
}

export function getDailyBenefitAmountForEnvironment(
  ltcPolicy: PolicyFundingSource,
  careEnvironment: CareEnvironment,
): number {
  // An explicitly-set value (including 0) is authoritative for the
  // environment — an explicit 0 means "not covered here" and is honored. A
  // null/undefined field means "not populated on import": legacy and CM II
  // rows commonly carry only one of the four daily fields, so we fall back to
  // the max populated value rather than projecting $0 for an otherwise-covered
  // home/AL/IL phase. Mirrors the legacy max-benefit fallback.
  const resolve = (configured: number | null | undefined): number =>
    configured ?? getMaxDailyBenefitAmount(ltcPolicy);

  switch (careEnvironment) {
    case CareEnvironment.home:
      return resolve(ltcPolicy.dailyHomeBenefitAmount);
    case CareEnvironment.assistedLiving:
      return resolve(ltcPolicy.dailyAssistedLivingBenefitAmount);
    case CareEnvironment.fullCareFacility:
      return resolve(ltcPolicy.dailyNursingHomeBenefitAmount);
    case CareEnvironment.independentLiving:
      // Reimbursement policies do not cover Independent Living. Zero out the
      // benefit for shop-insurance policies so per-environment payout caps and
      // downstream totals reflect the product reality. Mirrors the carve-out on
      // the legacy path in calcFundingSourceDerivatives.ts. Manually-added
      // policies respect whatever the advisor entered (with the same sparse-
      // field fallback as the other environments).
      //
      // TODO: Persist `dailyIndependentLivingBenefitAmount = 0` at funding-
      // source creation time for shop-insurance reimbursement policies
      // (policyQuoteCalculations.ts:170, create-traditional-ltc-funding-
      // source.ts:39, build-policy-funding-source.ts:51, and the equivalent
      // sites in calcClientROIProjections.ts). Today they seed IL equal to
      // the other care settings, forcing every display layer to mask it via
      // this helper or the form's `convertFromDaily={() => 0}` shim. Once IL
      // persists as 0 on the stored row, this carve-out (and the form shim)
      // can be removed — the raw field becomes truthful and every reader
      // shows the correct value without an opinion.
      if (!ltcPolicy.isIndemnityPolicyPayment && ltcPolicy.addedViaShopInsurance) {
        return 0;
      }
      return resolve(ltcPolicy.dailyIndependentLivingBenefitAmount);
    default:
      return 0;
  }
}

export function getMonthlyBenefitAmountForEnvironment(
  ltcPolicy: PolicyFundingSource,
  careEnvironment: CareEnvironment,
): number {
  return getDailyBenefitAmountForEnvironment(ltcPolicy, careEnvironment) * DAYS_IN_MONTH;
}

export function getWaitingPeriodBucketForEnvironment(
  careEnvironment: CareEnvironment,
): 'homeDays' | 'facilityDays' {
  return careEnvironment === CareEnvironment.home ? 'homeDays' : 'facilityDays';
}

export interface WaitingPeriodConsumptionResult {
  openRelevantWp: number;
  closeRelevantWp: number;
  wpDaysThisRow: number;
  closeHomeWp: number;
  closeFacilityWp: number;
  waitingPeriodDaysConsumedHome: number;
  waitingPeriodDaysConsumedFacility: number;
}

export function consumeWaitingPeriodDaysForEnvironment(
  waitingPeriod: { homeDays: number; facilityDays: number },
  careEnvironment: CareEnvironment,
  daysAfter2Adl: number,
): WaitingPeriodConsumptionResult {
  const bucket = getWaitingPeriodBucketForEnvironment(careEnvironment);
  const openHomeWp = Math.max(waitingPeriod.homeDays, 0);
  const openFacilityWp = Math.max(waitingPeriod.facilityDays, 0);
  const openRelevantWp = bucket === 'homeDays' ? openHomeWp : openFacilityWp;
  const daysServed = Math.max(daysAfter2Adl, 0);

  // The elimination period is a single, claim-wide clock measured in *unpaid
  // waiting days* — days the insured needs care but the policy is not yet
  // paying. Only the active environment's still-open EP produces waiting days
  // this row (`wpDaysThisRow`). Once that environment's EP is satisfied the
  // row pays, so no waiting days accrue and the clock stops for *both*
  // buckets.
  //
  // Those waiting days decrement both buckets in tandem (each clamped to its
  // own balance). Consequences:
  //  - Equal WPs (home 90 / facility 90) satisfy together, so a later care-
  //    environment change never restarts the waiting period.
  //  - Unequal WPs (home 60 / facility 90) share the served days but keep the
  //    longer threshold: after 60 waiting days home pays, facility still has
  //    30 to go — served when facility care later begins (it is NOT burned
  //    down by the paying home-care months in between).
  //  - A 0-day environment (e.g. Asset Care's home EP) produces no waiting
  //    days, so it never advances the other bucket — facility then serves its
  //    full EP when facility care begins.
  const wpDaysThisRow = Math.min(daysServed, openRelevantWp);
  const waitingPeriodDaysConsumedHome = Math.min(wpDaysThisRow, openHomeWp);
  const waitingPeriodDaysConsumedFacility = Math.min(wpDaysThisRow, openFacilityWp);
  const closeHomeWp = openHomeWp - waitingPeriodDaysConsumedHome;
  const closeFacilityWp = openFacilityWp - waitingPeriodDaysConsumedFacility;
  const closeRelevantWp = bucket === 'homeDays' ? closeHomeWp : closeFacilityWp;

  return {
    openRelevantWp,
    closeRelevantWp,
    wpDaysThisRow,
    closeHomeWp,
    closeFacilityWp,
    waitingPeriodDaysConsumedHome,
    waitingPeriodDaysConsumedFacility,
  };
}

export function getRiderForm(ltcPolicy: PolicyFundingSource): CareMattersRiderForm {
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    rate: ltcPolicy.policyInflationProtection ?? 0,
    simple: ltcPolicy.isSimpleInflationProtection === true,
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : null,
  };
}

// Mirrors legacy `getCareMattersBenefitPeriodMonths`. Pre-PR CM rows lack
// `policyBenefitPeriodMonths`; fall back to the source quote so legacy
// rows still produce a non-zero base pool. Without this fallback the
// pool initializes to 0 and the strategy degenerates to rider-only.
export function getBenefitPeriodMonths(ltcPolicy: PolicyFundingSource): number {
  if (
    ltcPolicy.policyBenefitPeriodMonths !== null &&
    ltcPolicy.policyBenefitPeriodMonths !== undefined
  ) {
    return ltcPolicy.policyBenefitPeriodMonths;
  }
  const rawQuote: unknown = ltcPolicy.sourceQuote;
  if (!rawQuote) return 0;
  let quote: unknown = rawQuote;
  if (typeof quote === 'string') {
    try {
      quote = JSON.parse(quote);
    } catch {
      return 0;
    }
  }
  if (quote && typeof quote === 'object' && 'ltcBenefitDurationMonths' in quote) {
    const value = (quote as { ltcBenefitDurationMonths?: unknown }).ltcBenefitDurationMonths;
    return typeof value === 'number' ? value : 0;
  }
  return 0;
}

// Calendar-anchored policy year for a projection row. Anchors on the first
// phase's policy start year and advances strictly by `monthIndex / 12`, so
// the year increments on every 12th projection row regardless of phase
// boundaries. Use this for display + for any per-calendar-year accrual
// (e.g. Asset Flex ACIO, future products whose riders escalate on policy
// anniversary).
//
// Not appropriate for products whose rider/benefit year is keyed to paid
// months rather than calendar months — CM II's paying rows use
// `policyYearForPayingRow` (a paid-month-walking formula) instead, since
// the rider "pauses" during waiting-period months.
//
// Assumes phases are contiguous in calendar time. Intentional calendar
// gaps between phases (rare) would undercount years here.
export function getCalendarPolicyYear(client: Client, monthIndex: number): number {
  const phaseCarePlans = client.projectionInputs?.phaseCarePlans ?? [];
  if (phaseCarePlans.length === 0) return 1;
  const firstProjectedCarePhase = client.projectionInputs?.monthlyCareInputs?.[0]?.carePhase;
  const anchorPhasePlan =
    (firstProjectedCarePhase != null
      ? phaseCarePlans.find(plan => plan.carePhase === firstProjectedCarePhase)
      : null) ?? phaseCarePlans[0];
  const firstPhasePolicyStartYear = Math.floor(
    getPhasePolicyStartYear(client, anchorPhasePlan),
  );
  return firstPhasePolicyStartYear + Math.floor(monthIndex / 12);
}

// Calendar policy year for a projection row. Single-life derives the anchor
// from the client's own first projected phase (`getCalendarPolicyYear`). Joint
// runs pass an explicit shared anchor — the policy year at monthIndex 0, i.e.
// the earliest claim start across both spouses — so both spouses sit on one
// monotonic policy-year clock (plan A3). The session client is not a reliable
// anchor for joint because the partner may claim first.
export function resolveRowPolicyYear(
  client: Client,
  monthIndex: number,
  jointAnchorPolicyYear: number | undefined,
): number {
  // Joint anchor + calendar offset, UNCLAMPED. `jointAnchorPolicyYear` is the
  // integer policy year at monthIndex 0 (= Jan 1 of the earliest spouse's onset
  // year; see `buildJointProjectionContext`), and `monthIndex` is measured from
  // that same Jan-1 origin, so `anchor + floor(monthIndex/12)` lands every row on
  // its true calendar policy year by integer math — no fractional-floor drift.
  // In-force rows are naturally >= 1; rows whose care predates the policy's
  // premium-start year resolve to <= 0 and are exactly the set the pre-policy
  // gate catches (those months pay $0 and book the full cost out-of-pocket). An
  // earlier `Math.max(1, …)` clamp here collapsed policy-year 0 and 1 onto "1",
  // which both masked pre-policy months and suppressed the first real
  // anniversary; the gate supersedes it. Single-life still floors via
  // `getPhasePolicyStartYear`'s own `Math.max(1, ...)`, so it cannot reach < 1.
  return jointAnchorPolicyYear != null
    ? jointAnchorPolicyYear + Math.floor(monthIndex / 12)
    : getCalendarPolicyYear(client, monthIndex);
}

// Anniversary-compound factor used by asset-based hybrid strategies whose
// pools and per-month cap inflate on every policy anniversary. `policyYear = 1`
// is the at-issue base (factor 1.0); the benefit then steps up on each policy
// anniversary. A `capYears`-year inflation rider applies `capYears` annual
// increases — matching carrier illustrations (e.g. OneAmerica Asset Care's
// "20 year period": the AOB/COB monthly cap grows through policy year 21 to
// `(1+rate)^20`, then freezes). So the exponent equals `policyYear - 1` until
// it caps at `capYears`, holding `(1+rate)^(capYears)` for every later year.
// `capYears <= 0` means uncapped.
export function compoundFactor(
  rate: number,
  capYears: number,
  policyYear: number,
): number {
  const rawExponent = Math.max(policyYear - 1, 0);
  const exponent = capYears > 0 ? Math.min(rawExponent, capYears) : rawExponent;
  return Math.pow(1 + rate, exponent);
}

// Care-cost inflation anchor for a client. Year of `clientAddedDateTime`,
// with a same-day fallback when the field is missing. The whole app uses
// year-only granularity for this anchor — keep that consistent if you ever
// switch to fractional precision.
export function getCareCostAnchorYear(client: Client): number {
  return client.clientAddedDateTime
    ? client.clientAddedDateTime.getFullYear()
    : new Date().getFullYear();
}

// Predicted calendar year of the first contributing phase (in care-phase
// order). Mirrors how `calcMonthlyCareInputs` starts the row stream at the
// first non-zero phase. Falls back to the cost anchor when no phase
// contributes months — keeps inflation at 0 instead of blowing up.
export function getFirstContributingPhaseStartYear(client: Client): number {
  const phasePlans = client.projectionInputs?.phaseCarePlans ?? [];
  const firstContributing = phasePlans.find(
    p => Math.round(p.phaseCareMonthsNeeded) > 0,
  );
  return (
    (firstContributing &&
      client.clientPhasePredictedStartYears?.[firstContributing.carePhase]) ??
    getCareCostAnchorYear(client)
  );
}

// Per-phase care duration map sourced from `client.projectionInputs.phaseCarePlans`.
// Surfaced on `ClaimTerms.phaseCareMonthsByPhase` so the projection-details UI
// can derive phase-scoped quantities (e.g. the <2 ADL bucket size) without
// having to plumb the raw client through. Phases the plan doesn't visit are
// absent (Partial), not zero-valued — distinguishes "no plan" from "plan with
// 0 months".
export function getPhaseCareMonthsByPhase(
  client: Client,
): Partial<Record<CarePhase, number>> {
  const plans = client.projectionInputs?.phaseCarePlans ?? [];
  const out: Partial<Record<CarePhase, number>> = {};
  for (const plan of plans) {
    out[plan.carePhase] = plan.phaseCareMonthsNeeded;
  }
  return out;
}

export function getPhasePolicyStartYear(
  client: Client,
  phaseCarePlan: PhaseCarePlan,
): number {
  const policyPremiumStartYear = client.fundingSources?.ltcPolicy?.policyPremiumStartYear;
  if (isNullOrUndefined(policyPremiumStartYear)) {
    throw new Error('Policy premium start year is not defined');
  }
  const carePhase = phaseCarePlan.carePhase;
  const startCalendarYear = client.clientPhasePredictedStartYears?.[carePhase];
  const endCalendarYear = client.clientPhasePredictedEndYears?.[carePhase];
  const phasePredictedStartYear =
    startCalendarYear ??
    Math.max(
      (endCalendarYear ?? policyPremiumStartYear) -
        Math.ceil(phaseCarePlan.phaseCareMonthsNeeded / 12),
      policyPremiumStartYear,
    );
  return Math.max(1, phasePredictedStartYear - policyPremiumStartYear + 1);
}
